import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import Pagination from './Pagination'
import UserReviewTile from './UserReviewTile'
import LineFilterAltIcon from 'components/Luxkit/Icons/line/LineFilterAltIcon'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { fetchCustomerReviewSummary, fetchUserReviews } from 'actions/ReviewActions'
import { SORT_OPTION_MOST_RELEVANT, userReviewsSortOptions } from 'constants/review'
import { scrollToId } from 'lib/scrollUtils'
import useToggle from 'hooks/useToggle'
import UserReviewFilters from './UserReviewFilters'
import UserReviewSort from './UserReviewSort'
import UserReviewEmptyState from './UserReviewEmptyState'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { Divider } from 'components/OfferPage/BnblModal/Components/Shared/Divider'
import PropertyReviewAiSummary from 'components/OfferPage/NavigableOfferContent/PropertyReviewAiSummary'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};

  > *:nth-child(2) {
    overflow: hidden;
    padding-left: ${rem(10)};
  }

  ${mediaQueryUp.sm} {
    flex-direction: row;
    > *:first-child {
      min-width: ${rem(240)};
    }
  }
`

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`

const INITIAL_PAGINATION = { limit: 4, offset: 0 }
const INITIAL_FILTERS = { ratings: [], travellers: [], months: [] }

interface LocalFilters {
  ratings?: string[];
  travellers?: string[];
  months?: string[];
}

interface LocalPagination {
  limit: number;
  offset: number;
}

interface MappedProps {
  userReviews: App.Review[];
  fetchingReviews: boolean;
  totalReviews: number;
  fetchingReviewSummary: boolean;
  reviewSummary: App.ReviewSummary | undefined;
}

interface Props {
  userReviews: App.Review[];
  property?: App.OfferProperty | App.BedbankProperty;
  isLPP?: boolean;
  experience?: App.ExperienceOffer;
  tour?: Tours.V2Offer;
}

function UserReviewList({
  userReviews,
  property,
  fetchingReviews,
  totalReviews,
  isLPP,
  experience,
  fetchingReviewSummary,
  reviewSummary,
  tour,
}: Props & MappedProps) {
  const [showFilters, , openFilters, closeFilters] = useToggle()
  const [localPagination, setLocalPagination] = useState<LocalPagination>(INITIAL_PAGINATION)
  const [filters, setFilters] = useState<LocalFilters>(INITIAL_FILTERS)
  const [totalFilters, setTotalFilters] = useState<number>(0)
  const [localPage, setLocalPage] = useState<number>(0)
  const [sort, setSort] = useState(SORT_OPTION_MOST_RELEVANT.value)

  const dispatch = useAppDispatch()

  const ratingData = {
    rating: property?.rating ?? experience?.reviewsRating ?? tour?.reviewsRating ?? 0,
    reviewsTotal: property?.reviewsTotal ?? experience?.reviewsTotal ?? tour?.reviewsTotal ?? 0,
    reviewsSource: property?.reviewsSource ?? experience?.reviewsSource ?? tour?.reviewsSource,
  }

  const isGoogleReviews = ratingData.reviewsSource === 'google'

  const onFetchUserReviews = useCallback((params: { limit?, offset?, filters?, sort? }) => {
    const {
      limit,
      offset,
      filters,
      sort,
    } = params

    // If user changes the page, scroll to top
    if (offset !== localPagination.offset) {
      scrollToId('customer-reviews', -54)
    }

    setLocalPage(offset / limit)
    setLocalPagination({ limit, offset })

    dispatch(fetchUserReviews({
      ...(property && { propertyId: property.id }),
      ...(experience && { experienceId: experience.id }),
      ...(tour && { tourId: tour.id }),
      ...(isLPP && { propertyProvider: 'bedbank' }),
      useExternalReviews: true,
      limit,
      offset,
      ...filters,
      sort,
    }))
  }, [dispatch, property, localPagination, experience, tour, isLPP])

  const onFiltersApply = useCallback((newFilters: LocalFilters) => {
    const newTotal = Object.values(newFilters).reduce((acc, item) => acc + item.length, 0)
    setTotalFilters(newTotal)
    setFilters(newFilters)

    let newSort = sort

    // If user add some ratings filter, set sort to Best rating
    const newRatingFilters = newFilters.ratings ?? []
    const ratingFilters = filters.ratings ?? []
    if (newRatingFilters.length > ratingFilters.length && sort !== SORT_OPTION_MOST_RELEVANT.value) {
      setSort(SORT_OPTION_MOST_RELEVANT.value)
      newSort = SORT_OPTION_MOST_RELEVANT.value
    }

    onFetchUserReviews({ ...localPagination, sort: newSort, filters: newFilters, offset: 0 })
  }, [onFetchUserReviews, localPagination, sort, filters])

  const onSortSelect = useCallback((newSort) => {
    setSort(newSort)

    onFetchUserReviews({ ...localPagination, filters, sort: newSort, offset: 0 })
  }, [onFetchUserReviews, localPagination, filters])

  const onResetFilters = useCallback(() => {
    setTotalFilters(0)
    setFilters(INITIAL_FILTERS)

    onFetchUserReviews({ ...INITIAL_PAGINATION, filters: INITIAL_FILTERS, sort: SORT_OPTION_MOST_RELEVANT.value })
  }, [onFetchUserReviews])

  useEffect(() => {
    onFetchUserReviews({ ...INITIAL_PAGINATION, sort: 'best_rating' })
    if (property) {
      const propertyProvider = isLPP ? 'bedbank' : 'luxuryescapes'
      dispatch(fetchCustomerReviewSummary(property.id, propertyProvider))
    }
    // onFetchUserReviews is not tracked because it gets reset for inifinity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property, isLPP, dispatch])

  return (
    <Root>
      <div>
        <NumberRating
          variant="xl"
          rating={ratingData.rating}
          total={ratingData.reviewsTotal}
          ratingSubtitle={isGoogleReviews ? 'Reviews from Google' : undefined}
        />
      </div>
      <Group direction="vertical" fullWidth gap={24}>
        {reviewSummary && reviewSummary.summary && !isGoogleReviews && ratingData.reviewsTotal > 19 &&
          <PropertyReviewAiSummary content={reviewSummary.summary}/>
        }
        {fetchingReviewSummary && <>
          <LoadingIndicator />
          <Divider />
        </>}
        <Group direction="horizontal" gap={8}>
          <UserReviewSort
            sortOptions={userReviewsSortOptions}
            sortValue={sort}
            onSelect={onSortSelect}
          />
          {!isGoogleReviews && (
            <AssistChip size="medium" variant="filled" startIcon={<LineFilterAltIcon />} onClick={openFilters}>
              All filters {totalFilters > 0 && `(${totalFilters})`}
            </AssistChip>
          )}
        </Group>
        {!userReviews.length && !fetchingReviews && (<UserReviewEmptyState onResetFilter={onResetFilters} />)}
        {!!userReviews.length && (
          <Pagination
            label="Reviews"
            total={totalReviews}
            itemsPerPage={4}
            fetching={fetchingReviews}
            fetch={onFetchUserReviews}
            filters={filters}
            sort={sort}
            localPage={localPage}
          >
            <ReviewsList>
              {userReviews.map(review => (
                <UserReviewTile
                  key={review.id}
                  userReview={review}
                />
              ))}
            </ReviewsList>
          </Pagination>
        )}
      </Group>

      <UserReviewFilters
        open={showFilters}
        onApply={onFiltersApply}
        onClose={closeFilters}
        existingFilters={filters}
      />
    </Root>
  )
}

const mapStateToProps = (state: App.State): MappedProps => ({
  userReviews: state.review.reviews,
  fetchingReviews: state.review.fetchingReviews,
  totalReviews: state.review.totalReviews,
  fetchingReviewSummary: state.review.fetchingReviewSummary,
  reviewSummary: state.review.reviewSummary,
})

export default connect(mapStateToProps)(UserReviewList)
